import type { Deal } from "@/api/client";
import type { PartnerQuickAction } from "@/components/deals/dealDetailStatus";
import {
  DealExtensionRequestAction,
  DealExtensionWithdrawAction,
} from "@/components/deals/DealExtensionRequestForm";
import { DealProfileSections } from "@/components/deals/DealProfileSections";
import { DealProgressSection } from "@/components/deals/DealProgressSection";
import { DealSpifTracker } from "@/components/deals/DealSpifTracker";
import { DealStatusSection } from "@/components/deals/DealStatusSection";
import { DealWinActions } from "@/components/deals/DealWinActions";
import { DetailSections } from "@/components/ui/section";

/**
 * The BDM/PO deal detail body (RACCPE-21): Status → Actions (live only) →
 * The Deal. Shared by the standalone `/deals/$dealId` route and the slide-out
 * drawer so the two can't drift; each host renders its own header chrome
 * ([Client Name] title + [Deal ID] subtitle) and supplies `onActionDone`
 * (route → navigate to the dashboard; drawer → close) for the CHP-234 actions.
 */
export function DealDetailContent({
  deal,
  onActionDone,
  initialAction,
  showDealId = true,
  readOnly = false,
}: {
  deal: Deal;
  onActionDone: () => void;
  /** Grid Quick Action that opened this view — auto-opens the matching
   * confirm dialog in DealWinActions (live deals only). */
  initialAction?: PartnerQuickAction | null;
  /** Whether the Opportunity "Deal ID" field renders. The standalone route
   * host shows the Deal ID in its own header, so it passes false to avoid the
   * duplicate; the drawer keeps it (CHP-806). */
  showDealId?: boolean;
  /**
   * CHP-808: suppress the owner-only write actions (Log deal progress / Request
   * Closed-Won / Mark Closed-Lost / Request extension) and the Deal-Progress
   * log button. Set when a non-owner (a manager/PO viewing the master record
   * from an insights explorer) opens this view — the backend note/win/loss
   * endpoints are owner/creator-only and 404 a non-owner, so the affordances
   * would be a dead-end. The activity log itself stays visible (read-only).
   */
  readOnly?: boolean;
}) {
  return (
    <DetailSections>
      <DealStatusSection deal={deal} viewer="partner" />

      {(deal.deal_status === "live" || deal.deal_status === "extension_pending") && !readOnly ? (
        <section>
          <h2 className="mb-3 text-base font-semibold">Actions</h2>
          <div className="space-y-3">
            {deal.deal_status === "live" ? (
              /* CHP-429: action order is Log deal progress (highlighted) →
                 Request Closed-Won → Mark as closed-lost → Request extension
                 (passed as the trailing slot so it reads last in the row). */
              <DealWinActions
                deal={deal}
                onDone={onActionDone}
                initialAction={initialAction}
                trailing={<DealExtensionRequestAction deal={deal} />}
              />
            ) : (
              /* CHP-1179: an extension-pending deal's only owner action is
                 withdrawing the request — the win/close actions are live-only
                 and would 409. */
              <DealExtensionWithdrawAction deal={deal} />
            )}
          </div>
        </section>
      ) : null}

      <section>
        <h2 className="mb-4 text-base font-semibold">The Deal</h2>
        <DetailSections>
          {/* CHP-1034: the partner detail has no Channel Partner section, so
              it opts into the Opportunity block's "Deal owner" field. */}
          <DealProfileSections deal={deal} showDealId={showDealId} showOwner />
        </DetailSections>
      </section>

      {/* Deal Progress (CHP-429, renamed from "Notes") — the BDM/PO activity
          log. The CM/RA/RM view keeps DealNotesSection ("Deal Owner Notes",
          CHP-401). CHP-808: readOnly hides the "Log deal progress" button for a
          non-owner (the log stays visible). */}
      <DealProgressSection deal={deal} readOnly={readOnly} />

      {/* SPIF tracker (gap-build W2) — renders only once SPIF state exists. */}
      <DealSpifTracker deal={deal} />
    </DetailSections>
  );
}
